import React from 'react';
import { Box } from '@material-ui/core';
import ClassicEditor from 'ckeditor5-custom-build';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { getRandomBackgroundColor, getUserName, getRandomColor, decryptResponse } from '../../../config/utils';
import { sanitizeWithBashCheck } from '../utils';
import BashScriptAlert from './BashScriptAlert';
import { UPLOAD_NOTES_FILES, UPLOAD_CONVERSATION_FILES } from '../../../config/api-url';
import { getHeaders } from '../../../config/auth';
import * as EmojiJson from './EmojiJson';
import { defaultToolbar, ImageUploadToolbar, EmoticonsToolbar } from './CkEditorEnums';

export function SpecialCharactersEmoji(editor) {
	editor.ui.componentFactory.add('localizedSpecialCharacter', (locale) => {
		const fontButton = editor.ui.componentFactory.create('specialCharacters');
		fontButton.buttonView.label = 'Emoji';
		fontButton.buttonView.tooltip = 'Emoji';
		fontButton.buttonView.icon = '';
		fontButton.buttonView.class = 'ow-icon-editor-emoji';
		fontButton.buttonView.icon =
			"<svg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'> 		<path 			d='M12.6719 24.2314C19.2993 24.2314 24.6719 18.8589 24.6719 12.2314C24.6719 5.60403 19.2993 0.231445 12.6719 0.231445C6.04446 0.231445 0.671875 5.60403 0.671875 12.2314C0.671875 18.8589 6.04446 24.2314 12.6719 24.2314Z' 			fill='#FFD700' 		/> 		<path 			d='M12.6719 19.9442C9.16261 19.9442 6.10486 17.5674 5.23636 14.1654C5.16811 13.8977 5.32936 13.6254 5.59711 13.5572C5.86336 13.4874 6.13636 13.6502 6.20461 13.9179C6.96061 16.8774 9.61936 18.9444 12.6711 18.9444C15.7229 18.9444 18.3824 16.8774 19.1376 13.9179C19.2051 13.6502 19.4774 13.4874 19.7459 13.5572C20.0136 13.6254 20.1749 13.8977 20.1066 14.1654C19.2389 17.5674 16.1811 19.9442 12.6719 19.9442Z' 			fill='#3E4347' 		/> 		<path 			d='M24.6723 12.2319C24.6723 18.8619 19.2986 24.2319 12.6723 24.2319C8.8998 24.2319 5.5323 22.4919 3.33105 19.7656C5.3898 21.4306 8.01105 22.4244 10.8648 22.4244C17.4911 22.4244 22.8648 17.0544 22.8648 10.4244C22.8648 7.57063 21.8711 4.94938 20.2061 2.89062C22.9286 5.09188 24.6723 8.45938 24.6723 12.2319Z' 			fill='black' 			fill-opacity='0.05' 		/> 		<path 			d='M8.79701 12.744C9.81266 12.744 10.636 11.7656 10.636 10.5585C10.636 9.35153 9.81266 8.37305 8.79701 8.37305C7.78136 8.37305 6.95801 9.35153 6.95801 10.5585C6.95801 11.7656 7.78136 12.744 8.79701 12.744Z' 			fill='#3E4347' 		/> 		<path 			d='M9.62593 10.0881C9.48268 10.2006 9.24718 10.1451 9.09643 9.96208C8.94568 9.77908 8.93818 9.53383 9.08143 9.42133C9.22393 9.30358 9.46018 9.35907 9.61018 9.54208C9.76168 9.73033 9.76318 9.97108 9.62593 10.0881Z' 			fill='#5A5F63' 		/> 		<path 			d='M16.549 12.744C17.5646 12.744 18.388 11.7656 18.388 10.5585C18.388 9.35153 17.5646 8.37305 16.549 8.37305C15.5333 8.37305 14.71 9.35153 14.71 10.5585C14.71 11.7656 15.5333 12.744 16.549 12.744Z' 			fill='#3E4347' 		/> 		<path 			d='M17.3769 10.0881C17.2337 10.2006 16.9982 10.1451 16.8474 9.96208C16.6967 9.77908 16.6892 9.53383 16.8324 9.42133C16.9749 9.30358 17.2104 9.35907 17.3612 9.54208C17.5127 9.73033 17.5142 9.97108 17.3769 10.0881Z' 			fill='#5A5F63' 		/> 	</svg>";
		return fontButton;
	});
	editor.plugins.get('SpecialCharacters').addItems('Emoji', EmojiJson.symbolIcons, { label: 'Emoji' });
}

function MentionCustomization(editor) {
	// The upcast converter will convert view <a class="mention" href="" data-user-id="">
	// elements to the model 'mention' text attribute.
	editor.conversion.for('upcast').elementToAttribute({
		view: {
			name: 'a',
			key: 'data-mention',
			classes: 'mention',
			attributes: {
				//href: true,
				'data-user-id': true,
			},
		},
		model: {
			key: 'mention',
			value: (viewItem) => {
				// The mention feature expects that the mention attribute value
				// in the model is a plain object with a set of additional attributes.
				// In order to create a proper object use the toMentionAttribute() helper method:
				const mentionAttribute = editor.plugins.get('Mention').toMentionAttribute(viewItem, {
					// Add any other properties that you need.
					link: viewItem.getAttribute('href'),
					userId: viewItem.getAttribute('data-user-id'),
				});
				return mentionAttribute;
			},
		},
		converterPriority: 'high',
	});

	// Downcast the model 'mention' text attribute to a view <a> element.
	editor.conversion.for('downcast').attributeToElement({
		model: 'mention',
		view: (modelAttributeValue, { writer }) => {
			// Do not convert empty attributes (lack of value means no mention).
			if (!modelAttributeValue) {
				return;
			}

			return writer.createAttributeElement(
				'a',
				{
					class: 'mention tagged-name',
					'data-mention': modelAttributeValue.id,
					'data-user-id': modelAttributeValue.userId,
					rel: modelAttributeValue.userId,
				},
				{
					// Make mention attribute to be wrapped by other attribute elements.
					priority: 20,
					// Prevent merging mentions together.
					id: modelAttributeValue.uid,
				}
			);
		},
		converterPriority: 'high',
	});
}

function customItemRenderer(item) {
	const itemElement = document.createElement('span');
	itemElement.classList.add('custom-item');
	itemElement.id = `mention-list-item-id-${item.userId}`;
	itemElement.textContent = '';

	let imageHtml = document.createElement('div');
	imageHtml.classList.add('avatar-default');
	if (item.imagePath) {
		const img = document.createElement('img');
		img.src = item.imagePath;
		imageHtml.appendChild(img);
	} else {
		imageHtml.style.backgroundColor = getRandomBackgroundColor(item.backGroundColorCode);
		imageHtml.style.color = getRandomColor(item.colorCode);
		imageHtml.textContent = getUserName(item);
	}

	itemElement.appendChild(imageHtml);

	const userWrap = document.createElement('div');
	userWrap.classList.add('search-user-info');

	const usernameElement = document.createElement('p');
	usernameElement.textContent = `${item.name} `;

	const usernameDes = document.createElement('span');
	usernameDes.classList.add('email-text');
	usernameDes.textContent = `${item.emailId} `;

	userWrap.appendChild(usernameElement);
	userWrap.appendChild(usernameDes);

	itemElement.appendChild(userWrap);
	return itemElement;
}

export default class CkEditor extends React.Component {
	constructor(props) {
		super(props);
		let toolbarOptions = props.toolbar ? props.toolbar : defaultToolbar;
		let mentionSettings = props.enableTagging
			? {
					mention: {
						feeds: [
							{
								marker: '@',
								feed: this.getFeedItems,
								itemRenderer: customItemRenderer,
								dropdownLimit: 10,
								minimumCharacters: 3,
							},
						],
					},
			  }
			: {};
		if (props.noToolbar) {
			toolbarOptions = [];
		} else {
			if (props.uploadImage) {
				toolbarOptions = [...toolbarOptions, ...ImageUploadToolbar];
			}
			if (props.showEmoticons) {
				toolbarOptions = [...toolbarOptions, ...EmoticonsToolbar];
			}
		}
		if (props.removedPluginsList && props.removedPluginsList.length > 0) {
			toolbarOptions = toolbarOptions.filter((item) => {
				return !props.removedPluginsList.includes(item);
			});
		}
		this.state = {
			editorValue: props.value,
			extraConfig: props.extraConfig ? props.extraConfig : {},
			toolbar: toolbarOptions,
			mentionSettings: mentionSettings,
			isBashScriptDetected: false,
			lastValidContent: props.value || '',
			showBashAlert: false,
			bashAlertMessage: '',
			bashAlertTitle: '',
		};
		this.editorInstance = null;
	}

	getFeedItems = async (text) => {
		//here
		let resp = await this.props.globalSearchAPIWithTeam(text, 1, 15, 1);
		let entityList = resp.data.entityList;
		if (entityList) {
			entityList = entityList.map((data) => {
				return {
					...data,
					id: data.searchType === 2 ? '@' + data.teamName : '@' + data.firstName + '' + data.lastName,
					userId: data.searchType === 2 ? data.teamId + '_teamId' : data.employeeId + '',
					name: data.searchType === 2 ? data.teamName : data.firstName + ' ' + data.lastName,
					value: data.searchType === 2 ? data.teamName : data.firstName + ' ' + data.lastName,
					imagePath: data.searchType === 2 ? data.teamLogoImagePath : data.imagePath,
					emailId: data.emailId ? data.emailId : '',
					fullName: data.searchType === 2 ? data.teamName : data.fullname,
				};
			});
		}
		return entityList;
	};

	imageUploadSuccess = (fileData) => {
		this.props.imageList.push(fileData);
	};

	handleCloseBashAlert = () => {
		this.setState({ showBashAlert: false, bashAlertMessage: '', bashAlertTitle: '' });
	};

	showBashAlertDialog = (title, message) => {
		this.setState({
			showBashAlert: true,
			bashAlertTitle: title,
			bashAlertMessage: message,
		});
	};

	render() {
		return (
			<Box className='ck-editor-box '>
				{
					<CKEditor
						id={this.props.id}
						className={'ck-editor-area'}
						editor={ClassicEditor}
						onError={(a) => {
							console.log('Error', a);
						}}
						onReady={(editor) => {
							this.editorInstance = editor;
							editor?.setData && this.props.value && editor.setData(this.props.value);
						}}
						data={this.props.value}
						config={{
							...this.state.extraConfig,
							toolbar: this.state.toolbar,
							removePlugins: [
								'SpecialCharactersEssentials',
								'SpecialCharactersArrows',
								'SpecialCharactersCurrency',
								'SpecialCharactersLatin',
								'SpecialCharactersMathematical',
								'SpecialCharactersText',
							],
							extraPlugins:
								this.props.uploadImage && this.props.enableTagging
									? [MentionCustomization, SpecialCharactersEmoji, MyCustomUploadAdapterPlugin]
									: this.props.uploadImage
									? [SpecialCharactersEmoji, MyCustomUploadAdapterPlugin]
									: this.props.enableTagging
									? [MentionCustomization, SpecialCharactersEmoji]
									: [SpecialCharactersEmoji],
							uploadUrl: this.props.uploadCase === 'CONVERSATION' ? UPLOAD_CONVERSATION_FILES : UPLOAD_NOTES_FILES,
							...this.state.mentionSettings,
							ImageUploadSuccess: {
								onUpload: this.imageUploadSuccess,
								uploadType: this.props.uploadType,
							},
							placeholder: this.props.placeHolderText || '',
							link: {
								defaultProtocol: 'http://',
								decorators: {
									addTargetToExternalLinks: {
										mode: 'automatic',
										callback: (url) => 1 === 1,
										attributes: {
											target: '_blank',
											rel: 'noopener noreferrer',
										},
									},
								},
							},
						}}
						onChange={(event, editor) => {
							const data = editor.getData();
							
							// Apply bash script detection globally
							const { sanitized, hasBash, hasOtherDanger } = sanitizeWithBashCheck(data);

							// If bash script was detected before and still detected, block the change
							if (this.state.isBashScriptDetected && hasBash) {
								this.showBashAlertDialog(
									'Security Alert',
									'<strong>Bash/Shell script content detected!</strong><br/><br/>Please remove the bash/shell script content before continuing to edit. This content has been blocked for security reasons.'
								);
								// Revert to last valid content
								if (this.editorInstance && this.state.lastValidContent !== data) {
									editor.setData(this.state.lastValidContent);
								}
								return;
							}

							// If bash script detected for the first time
							if (hasBash && !this.state.isBashScriptDetected) {
								this.setState({ isBashScriptDetected: true });
								this.showBashAlertDialog(
									'Security Warning',
									'Potentially unsafe shell script content was detected and automatically removed to protect the system.<br/><br/>Please review the content and ensure no bash/shell script content is included before continuing to edit.'
								);
								// Revert to last valid content
								if (this.editorInstance && this.state.lastValidContent !== data) {
									editor.setData(this.state.lastValidContent);
								}
								return;
							}

							// If bash script was detected before but now removed, clear the flag
							if (!hasBash && this.state.isBashScriptDetected) {
								this.setState({ isBashScriptDetected: false });
							}

							// Alert if DOMPurify removed other dangerous content
							if (hasOtherDanger && !hasBash) {
								this.showBashAlertDialog(
									'Security Alert',
									'<strong>Potentially dangerous content detected!</strong><br/><br/>Malicious HTML/JavaScript content has been detected and removed for security reasons.'
								);
							}

							// Store last valid content and pass to parent
							// Use original data when nothing dangerous was removed to preserve cursor position
							const contentToUse = hasOtherDanger ? sanitized : data;
							this.setState({ lastValidContent: contentToUse });
							this.props.handleEditorChange(contentToUse);
						}}
						onBlur={(event, editor) => {
							this.props.handleBlur && this.props.handleBlur();
						}}
						ImageUploadSuccess={this.imageUploadSuccess}
					/>
				}
				<BashScriptAlert
					open={this.state.showBashAlert}
					onClose={this.handleCloseBashAlert}
					message={this.state.bashAlertMessage}
					title={this.state.bashAlertTitle}
				/>
			</Box>
		);
	}
}

class MyUploadAdapter {
	constructor(loader, uploadUrl, uploadSuccess) {
		// The file loader instance to use during the upload. It sounds scary but do not
		// worry — the loader will be passed into the adapter later on in this guide.
		this.loader = loader;
		this.uploadUrl = uploadUrl;
		this.uploadSuccess = uploadSuccess;
	}
	// Starts the upload process.
	upload() {
		return this.loader.file.then(
			(file) =>
				new Promise((resolve, reject) => {
					this._initRequest(this.uploadSuccess);
					this._initListeners(resolve, reject, file, this.uploadSuccess);
					this._sendRequest(file);
				})
		);
	}

	// Aborts the upload process.
	abort() {
		if (this.xhr) {
			this.xhr.abort();
		}
	}
	// Initializes the XMLHttpRequest object using the URL passed to the constructor.
	_initRequest(uploadSuccess) {
		const xhr = (this.xhr = new XMLHttpRequest());

		// Note that your request may look different. It is up to you and your editor
		// integration to choose the right communication channel. This example uses
		// a POST request with JSON as a data structure but your configuration
		// could be different.
		xhr.open('POST', this.uploadUrl + `${uploadSuccess.uploadType ? '?type=' + uploadSuccess.uploadType : ''}`, true);
		xhr.responseType = ''; //'json';
	}
	_initListeners(resolve, reject, file, uploadSuccess) {
		const xhr = this.xhr;
		const loader = this.loader;
		const genericErrorText = `Couldn't upload file: ${file.name}.`;

		xhr.addEventListener('error', () => reject(genericErrorText));
		xhr.addEventListener('abort', () => reject());
		xhr.addEventListener('load', () => {
			const isEncryptionEnabled = !['local', 'dev'].includes(process.env.REACT_APP_ENV);
			const response = isEncryptionEnabled
				? JSON.parse(decryptResponse(xhr.response))
				: JSON.parse(xhr.response);
			// This example assumes the XHR server's "response" object will come with
			// an "error" which has its own "message" that can be passed to reject()
			// in the upload promise.
			//
			// Your integration may handle upload errors in a different way so make sure
			// it is done properly. The reject() function must be called when the upload fails.
			if (!response || response.error) {
				return reject(response && response.error ? response.error.message : genericErrorText);
			}
			uploadSuccess.onUpload({
				filePath: response.entity,
				fileName: file.name,
			});
			// If the upload is successful, resolve the upload promise with an object containing
			// at least the "default" URL, pointing to the image on the server.
			// This URL will be used to display the image in the content. Learn more in the
			// UploadAdapter#upload documentation.
			resolve({
				default: response.entity,
			});
		});

		// Upload progress when it is supported. The file loader has the #uploadTotal and #uploaded
		// properties which are used e.g. to display the upload progress bar in the editor
		// user interface.
		if (xhr.upload) {
			xhr.upload.addEventListener('progress', (evt) => {
				if (evt.lengthComputable) {
					loader.uploadTotal = evt.total;
					loader.uploaded = evt.loaded;
				}
			});
		}
	}
	_sendRequest(file) {
		const data = new FormData();
		data.append('UploadFiles', file);
		data.append('featureListType', 2);
		let headers = getHeaders();
		Object.keys(headers).forEach((value) => {
			this.xhr.setRequestHeader(value, headers[value]);
		});
		this.xhr.send(data);
	}
}

function MyCustomUploadAdapterPlugin(editor) {
	editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
		// Configure the URL to the upload script in your back-end here!
		return new MyUploadAdapter(loader, editor.config.get('uploadUrl'), editor.config.get('ImageUploadSuccess'));
	};
}
